import {
  Typography,
  Box,
  Button,
  Card,
  TextField,
} from '@mui/material'
import {
  CardAuth,
  BoxAuthHeading,
  BoxInputs,
  BoxAuthTyp,
  BoxAuthLink,
  CardReg,
  BoxRegTyp,
} from './style'
import { NavLink, useNavigate } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import {
  requestEmailCodeSchema,
  RequestEmailCodeFormValues,
  verifyEmailCodeSchema,
  VerifyEmailCodeFormValues
} from './ZodValidationSchema'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormBlock } from '../../../common/FormGenerator'
import { getFormConfig } from './settingsConfig'
import { useRequestEmailCodeMutation, useVerifyEmailCodeMutation } from '../../../api'
import { profileApi } from '../../../api/profile'
import { useAppDispatch } from '../../../hooks'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { TextFieldCode } from './style'
import { getSafeReturnPath } from '../../../utils/getSafeReturnPath'
import { AuthLegalConsent } from '../AuthLegalConsent'

type FormType = 'otp' | 'password_auth' | 'reg' | 'restore'

interface AuthFormProps {
  onSwitch: React.Dispatch<React.SetStateAction<FormType>>
  returnTo?: string
}

export const AuthForm: React.FC<AuthFormProps> = ({ onSwitch, returnTo }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')

  const emailMethods = useForm({
    mode: 'onBlur',
    resolver: zodResolver(requestEmailCodeSchema),
    defaultValues: {
      email: '',
    },
  })

  const codeMethods = useForm({
    mode: 'onBlur',
    resolver: zodResolver(verifyEmailCodeSchema),
    defaultValues: {
      code: '',
    },
  })

  const [
    requestEmailCode,
    {
      error: requestCodeError,
      isError: isRequestCodeError,
      isSuccess: isRequestCodeSuccess,
      isLoading: isRequestCodeLoading,
      reset: resetRequestCodeState
    },
  ] = useRequestEmailCodeMutation()

  const [
    verifyEmailCode,
    {
      data: verifyData,
      error: verifyCodeError,
      isError: isVerifyCodeError,
      isSuccess: isVerifyCodeSuccess,
      isLoading: isVerifyCodeLoading,
      reset: resetVerifyCodeState
    },
  ] = useVerifyEmailCodeMutation()

  const requestCodeInFlightRef = useRef(false)

  const onSubmitRequestCode = async (data: RequestEmailCodeFormValues) => {
    if (requestCodeInFlightRef.current || isRequestCodeLoading) {
      return
    }

    const normalizedEmail = data.email.trim().toLowerCase()
    requestCodeInFlightRef.current = true

    try {
      await requestEmailCode({ email: normalizedEmail }).unwrap()
      setEmail(normalizedEmail)
    } catch {
      // Ошибку показывает useEffect по isRequestCodeError.
    } finally {
      requestCodeInFlightRef.current = false
    }
  }

  const onSubmitVerifyCode = async (data: VerifyEmailCodeFormValues) => {
    if (isVerifyCodeLoading) {
      return
    }

    try {
      await verifyEmailCode({
        email,
        code: data.code,
      }).unwrap()
    } catch {
      // Ошибку показывает useEffect по isVerifyCodeError.
    }
  }

  useEffect(() => {
    if (isRequestCodeError) {
      toast.error('Не удалось отправить код. Попробуйте позже')
      resetRequestCodeState()
    }
  }, [requestCodeError, isRequestCodeError, resetRequestCodeState])

  useEffect(() => {
    if (isRequestCodeSuccess) {
      toast.success('Код отправлен')
      setStep('code')
      codeMethods.setFocus('code')
    }
  }, [isRequestCodeSuccess, codeMethods])

  useEffect(() => {
    if (isVerifyCodeError) {
      toast.error('Неверный или истекший код')
      resetVerifyCodeState()
    }
  }, [verifyCodeError, isVerifyCodeError, resetVerifyCodeState])

  useEffect(() => {
    if (!isVerifyCodeSuccess || !verifyData) {
      return
    }
    void (async () => {
      try {
        await dispatch(
          profileApi.endpoints.getProfile.initiate(undefined, { forceRefetch: true }),
        ).unwrap()
        navigate(getSafeReturnPath(returnTo), { replace: true })
      } catch {
        window.location.assign(getSafeReturnPath(returnTo))
      }
    })()
  }, [isVerifyCodeSuccess, verifyData, returnTo, dispatch, navigate])

  return (
    <Box>
      {step === 'email' ? (
        <FormProvider {...emailMethods}>
          <Box
            autoComplete="off"
            component="form"
            onSubmit={emailMethods.handleSubmit(onSubmitRequestCode)}
          >
            <Card variant="primaryCard" sx={CardAuth}>
              <Typography variant='h2' sx={BoxAuthHeading}>
                Введите email
              </Typography>
              <Box sx={BoxInputs}>
                <FormBlock formFieldConfig={getFormConfig('email')} />
              </Box>
              <Typography sx={BoxAuthTyp}>
                {/* Нужно использовать пароль?&nbsp; */}
                <NavLink
                  to='#'
                  style={BoxAuthLink}
                  onClick={(e) => {
                    e.preventDefault()
                    onSwitch('password_auth')
                  }}
                >
                  Войти по паролю
                </NavLink>
              </Typography>
              <Button variant="contained" color='primary' type='submit' disabled={isRequestCodeLoading}>
                Получить код
              </Button>
            </Card>
          </Box>
        </FormProvider>
      ) : (
        <FormProvider {...codeMethods}>
          <Box
            autoComplete="off"
            component="form"
            onSubmit={codeMethods.handleSubmit(onSubmitVerifyCode)}
          >
            <Card variant="primaryCard" sx={CardAuth}>
              <Typography variant='h2' sx={BoxAuthHeading}>
                Введите код из письма
              </Typography>
              <Box sx={BoxInputs}>
                <TextField
                  placeholder='Код из письма'
                  size='small'
                  sx={TextFieldCode}
                  fullWidth
                  {...codeMethods.register('code')}
                  error={Boolean(codeMethods.formState.errors.code)}
                  helperText={codeMethods.formState.errors.code?.message}
                />
              </Box>
              <Typography sx={BoxAuthTyp}>
                Код отправлен на <b>{email}</b>.&nbsp;
                <NavLink
                  to='#'
                  style={BoxAuthLink}
                  onClick={(e) => {
                    e.preventDefault()
                    if (!isRequestCodeLoading && email) {
                      requestEmailCode({ email })
                    }
                  }}
                >
                  Отправить код повторно
                </NavLink>
              </Typography>
              <Button variant="contained" color='primary' type='submit' disabled={isVerifyCodeLoading}>
                Войти
              </Button>
            </Card>
          </Box>
        </FormProvider>
      )}

      <Card variant='primaryCard' sx={CardReg}>
        <Typography sx={BoxRegTyp}>
          Для авторизации или регистрации используйте email и затем введите код из письма.
        </Typography>
      </Card>
      <AuthLegalConsent />
    </Box>
  )
}
